import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const AssignLeaves = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [leavesPerMonth, setLeavesPerMonth] = useState("");
  const [durationMonths, setDurationMonths] = useState("");
  const [leaveType, setLeaveType] = useState("");
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const navigate = useNavigate();

  // 🔐 Authentication check
 useEffect(() => {
  const token = localStorage.getItem("token");
  const roles = JSON.parse(localStorage.getItem("roles") || "[]");

  if (!token) {
    toast.error("You must be logged in to access this page.");
    navigate("/");
    return;
  }

  // ✅ check admin role
  if (!roles.includes("ROLE_ADMIN")) {
    toast.error("You are not authorized to access this page.");
    navigate("/");
    return;
  }
}, [navigate]);


  // ✅ Fetch employees
  useEffect(() => {
  const token = localStorage.getItem("token");
  fetch(`${API_BASE_URL}/api/admin/employees/active`, {
    headers: { Authorization: "Bearer " + token },
  })
    .then((res) => res.json())
    .then((data) => {
      // 🔼 Sort employees ascending by name
      const sorted = [...data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setEmployees(sorted);
      setFilteredEmployees(sorted);
    })
    .catch((err) => console.error(err));
}, []);


  // ✅ Fetch available leave types
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/leave-types/all`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((res) => res.json())
      .then((data) => setAvailableLeaveTypes(data))
      .catch((err) => console.error("Failed to fetch leave types:", err));
  }, []);

  // ✅ Filter employees by search term
  useEffect(() => {
    const filtered = employees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEmployees(filtered);
    setCurrentPage(1);
  }, [searchTerm, employees]);

  const handleCheckboxChange = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id)
        ? prev.filter((empId) => empId !== id)
        : [...prev, id]
    );
  };

  const assignLeaves = () => {
    const token = localStorage.getItem("token");
    if (selectedEmployees.length === 0)
      return alert("⚠ Please select at least one employee.");
    if (!leavesPerMonth || !durationMonths || !leaveType)
      return alert("⚠ Please fill all fields.");

    const payload = {
      employeeIds: selectedEmployees,
      leavesPerMonth: parseFloat(leavesPerMonth),
      durationMonths: parseInt(durationMonths),
      leaveType,
    };

    fetch(`${API_BASE_URL}/api/admin/assign-leaves`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          alert("✅ Custom leave plan assigned!");
          navigate("/admindashboard");
        } else {
          alert("❌ Failed to assign leaves.");
        }
      })
      .catch((err) => console.error(err));
  };

  // Pagination logic
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div
        className="container-fluid bg-light min-vh-100"
        style={{ paddingTop: "130px" }}
      >
        {/* ✅ Header with List Button */}
        <div className="d-flex justify-content-between align-items-center mb-4 px-4">
          <h2 className="text-dark fw-bold mb-0">
            Assign Custom Leave Plan
          </h2>
          <button
            className="btn btn-sm btn-primary text-white px-2"
            onClick={() => navigate("/viewleavecredit")}
          >
            <i className="bi bi-list-ul me-2"></i>List
          </button>
        </div>

        {/* Input Section */}
        <div className="row g-3 justify-content-center mb-4 px-4">
          <div className="col-md-3">
            <select
              className="form-select"
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
            >
              <option value="">Select Leave Type</option>
              {availableLeaveTypes.length > 0 ? (
                availableLeaveTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))
              ) : (
                <option disabled>Loading...</option>
              )}
            </select>
          </div>

          <div className="col-md-3">
            <input
              type="number"
              step="0.5"
              className="form-control"
              placeholder="Leaves per Month"
              value={leavesPerMonth}
              onChange={(e) => setLeavesPerMonth(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <input
              type="number"
              className="form-control"
              placeholder="Duration (months)"
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
            />
          </div>
        </div>

        {/* Search Field */}
        <div className="row justify-content-center mb-3 px-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Employee Table */}
        <div className="table-responsive px-4">
          <table className="table table-hover align-middle border shadow-sm bg-white">
            <thead className="table-secondary">
              <tr>
                <th className="text-center" style={{ width: "5%" }}>
                  Select
                </th>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {currentEmployees.length > 0 ? (
                currentEmployees.map((emp) => (
                  <tr
                    key={emp.id}
                    className={
                      selectedEmployees.includes(emp.id)
                        ? "table-success"
                        : ""
                    }
                  >
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.id)}
                        onChange={() => handleCheckboxChange(emp.id)}
                      />
                    </td>
                    <td>{emp.name}</td>
                    <td>{emp.email}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => (
              <li
                key={i + 1}
                className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>

        {/* Bottom Buttons */}
        <div className="d-flex justify-content-center gap-3 mt-5 pb-5">
          <button className="btn btn-primary px-4" onClick={assignLeaves}>
            <i className="bi bi-save me-2"></i>Save
          </button>

          <button
            className="btn btn-secondary px-4"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-x-circle me-2"></i>Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default AssignLeaves;
